import * as THREE from 'three'
import { markRaw } from 'vue'
import DicePhysicsSim from './dicePhysicsSim.js'

const SETTLE_CORRECTION = 0.28

/**
 * Plays back a rigid-body throw.
 *
 * The trajectory is simulated up front by {@link DicePhysicsSim} — real
 * collisions with the floor and the ring's invisible walls — and this service
 * only advances through the recorded frames, interpolating between them so the
 * motion is identical at any frame rate.
 */
export class RollStateService {
  constructor() {
    this.reset()
    this.callbacks = {
      onStateChange: null,
      onRollComplete: null,
      onNumberChange: null
    }
    this.sim = null
    this.geometry = null
  }

  reset() {
    this.state = {
      position: markRaw(new THREE.Vector3(0, 0.76, 0)),
      quaternion: markRaw(new THREE.Quaternion()),
      rotation: markRaw(new THREE.Vector3()),
      isRolling: false,
      rollTime: 0,
      duration: 0,
      targetNumber: 20,
      isDeterministic: true,
      currentDisplayNumber: 1,
      lastNumberChange: 0,
      seed: 0,
      attempts: 0,
      status: 'idle'
    }

    this.restPosition = markRaw(new THREE.Vector3(0, 0.76, 0))
    this.frames = null
    this.frameDt = 1 / 120
    this.correction = null
    this.targetQuaternion = markRaw(new THREE.Quaternion())
  }

  /** Icosahedron data the simulation needs; supplied once by the scene. */
  setGeometry(geometry) {
    this.geometry = geometry
    this.sim = markRaw(new DicePhysicsSim(geometry))
  }

  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks }
  }

  setRestPosition(x, y, z) {
    this.restPosition.set(x, y, z)
    this.state.position.copy(this.restPosition)
  }

  startRoll(targetNumber, isDeterministic = true, options = {}) {
    if (!this.sim) {
      console.warn('RollStateService: geometry not set, cannot simulate roll')
      return this.state
    }

    const seed = options.seed ?? `${targetNumber}:${this.restPosition.x}`
    this.sim.setArenaHalf(options.half ?? 0)
    const throwResult = this.sim.rollFor(targetNumber, seed)

    this.frames = throwResult.frames
    this.frameDt = throwResult.dt

    this.state.seed = seed
    this.state.targetNumber = targetNumber
    this.state.isDeterministic = isDeterministic
    this.state.duration = throwResult.duration
    this.state.rollTime = 0
    this.state.isRolling = true
    this.state.attempts = throwResult.attempts
    this.state.lastNumberChange = 0
    this.state.currentDisplayNumber = 1
    this.state.status = 'rolling'

    // Only used if the simulation could not find an exact landing: the last
    // fraction of a second eases onto the correct face.
    this.correction = throwResult.exact ? null : this.buildCorrection(targetNumber)

    this.applyFrame(0)
    this.notifyStateChange()
    return this.state
  }

  buildCorrection(targetNumber) {
    const { faceNormals, faceNumbers } = this.geometry
    const index = faceNumbers.indexOf(targetNumber)
    if (index === -1) return null

    const last = this.frames[this.frames.length - 1]
    const from = new THREE.Quaternion(last[3], last[4], last[5], last[6])
    const normal = new THREE.Vector3().copy(faceNormals[index]).applyQuaternion(from)
    const lift = new THREE.Quaternion().setFromUnitVectors(normal, new THREE.Vector3(0, 1, 0))

    this.targetQuaternion.copy(lift).multiply(from)
    return { from, startTime: Math.max(0, this.state.duration - SETTLE_CORRECTION) }
  }

  /**
   * @param {number} deltaTime seconds since last frame
   * @param {number[]} faceNumbers face-index → printed number
   */
  update(deltaTime, faceNumbers) {
    if (!this.state.isRolling) return this.state

    this.state.rollTime += deltaTime
    const t = Math.min(this.state.rollTime, this.state.duration)

    this.applyFrame(t)
    this.updateDisplayNumber(faceNumbers)

    if (this.state.rollTime >= this.state.duration) {
      this.completeRoll()
    } else {
      this.notifyStateChange()
    }

    return this.state
  }

  applyFrame(time) {
    if (!this.frames || this.frames.length === 0) return

    const exact = time / this.frameDt
    const index = Math.min(this.frames.length - 1, Math.floor(exact))
    const next = Math.min(this.frames.length - 1, index + 1)
    const alpha = Math.min(1, Math.max(0, exact - index))

    const a = this.frames[index]
    const b = this.frames[next]

    this.state.position.set(
      THREE.MathUtils.lerp(a[0], b[0], alpha) + this.restPosition.x,
      THREE.MathUtils.lerp(a[1], b[1], alpha),
      THREE.MathUtils.lerp(a[2], b[2], alpha) + this.restPosition.z
    )

    const qa = new THREE.Quaternion(a[3], a[4], a[5], a[6])
    const qb = new THREE.Quaternion(b[3], b[4], b[5], b[6])
    this.state.quaternion.copy(qa).slerp(qb, alpha)

    if (this.correction && time > this.correction.startTime) {
      const k = Math.min(1, (time - this.correction.startTime) / SETTLE_CORRECTION)
      this.state.quaternion.slerp(this.targetQuaternion, k * k)
    }

    const euler = new THREE.Euler().setFromQuaternion(this.state.quaternion)
    this.state.rotation.set(euler.x, euler.y, euler.z)
  }

  /** Reads the face that is currently uppermost, so HUDs can flicker along. */
  updateDisplayNumber(faceNumbers) {
    if (this.state.rollTime - this.state.lastNumberChange < 0.08) return
    this.state.lastNumberChange = this.state.rollTime

    const normals = this.geometry?.faceNormals
    if (!normals) return

    let best = -Infinity
    let number = this.state.currentDisplayNumber

    normals.forEach((normal, index) => {
      const y = new THREE.Vector3().copy(normal).applyQuaternion(this.state.quaternion).y
      if (y > best) {
        best = y
        number = (faceNumbers || this.geometry.faceNumbers)[index]
      }
    })

    if (number !== this.state.currentDisplayNumber) {
      this.state.currentDisplayNumber = number
      this.notifyNumberChange()
    }
  }

  completeRoll() {
    this.state.isRolling = false
    this.applyFrame(this.state.duration)
    this.state.currentDisplayNumber = this.state.targetNumber
    this.state.status = 'settled'

    this.notifyNumberChange()
    this.notifyStateChange()
    this.notifyRollComplete(this.state.targetNumber)
  }

  forceComplete() {
    if (!this.state.isRolling) return
    this.state.rollTime = this.state.duration
    this.completeRoll()
  }

  getCurrentState() {
    return { ...this.state }
  }

  isCurrentlyRolling() {
    return this.state.isRolling
  }

  getCurrentNumber() {
    return this.state.currentDisplayNumber
  }

  getStatus() {
    return this.state.status
  }

  notifyStateChange() {
    this.callbacks.onStateChange?.(this.state)
  }

  notifyRollComplete(result) {
    this.callbacks.onRollComplete?.({
      number: result,
      isDeterministic: this.state.isDeterministic,
      targetNumber: this.state.targetNumber,
      rollTime: this.state.rollTime,
      seed: this.state.seed,
      attempts: this.state.attempts
    })
  }

  notifyNumberChange() {
    this.callbacks.onNumberChange?.(this.state.currentDisplayNumber)
  }

  dispose() {
    this.reset()
    this.sim = null
    this.geometry = null
    this.callbacks = {
      onStateChange: null,
      onRollComplete: null,
      onNumberChange: null
    }
  }
}

export default RollStateService
